import React from 'react';
import { cn } from '@/lib/utils';

type BadgeVariant = 'default' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'outline' | 'destructive' | 'high' | 'medium' | 'low' | 'pass' | 'warn' | 'fail' | 'critical' | 'ok' | 'unknown';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-primary/15 text-primary border-primary/30',
  secondary: 'bg-secondary text-secondary-foreground border-border',
  success: 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30',
  pass: 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30',
  ok: 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30',
  warning: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  warn: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  error: 'bg-destructive/15 text-destructive border-destructive/30',
  fail: 'bg-destructive/15 text-destructive border-destructive/30',
  critical: 'bg-destructive/15 text-destructive border-destructive/30',
  info: 'bg-primary/15 text-primary border-primary/30',
  outline: 'bg-transparent text-muted-foreground border-border',
  unknown: 'bg-transparent text-muted-foreground border-border',
  destructive: 'bg-destructive/15 text-destructive border-destructive/30',
  high: 'bg-destructive/15 text-destructive border-destructive/30',
  medium: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  low: 'bg-muted text-muted-foreground border-border',
};

const sizeClasses = {
  sm: 'px-1.5 py-px text-[10px]',
  md: 'px-2 py-px text-[10px]',
  lg: 'px-2.5 py-0.5 text-xs',
};

const STATUS_WORDS = ['critical', 'high', 'medium', 'low', 'warning', 'pass', 'fail', 'pending', 'ok', 'warn', 'unknown'];

export function Badge({ children, variant = 'default', size = 'md', className }: BadgeProps) {
  const childrenText = typeof children === 'string'
    ? (STATUS_WORDS.includes(children.toLowerCase()) ? capitalize(children) : children)
    : children;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded border font-medium leading-tight whitespace-nowrap',
        variantClasses[variant] ?? variantClasses.default,
        sizeClasses[size],
        className
      )}
    >
      {childrenText}
    </span>
  );
}
